// src/components/AddServiceScheduleBottomSheet.tsx
import React, { useCallback, useState } from 'react';
import { Alert, ScrollView, StyleSheet, View, Text } from 'react-native';
import {
  Button,
  TextInput as PaperTextInput,
  useTheme,
  Chip,
  SegmentedButtons,
} from 'react-native-paper';
import { DatePickerModal } from 'react-native-paper-dates';
import BaseBottomSheet from './BaseBottomSheet';
import { useAuth } from '../context/AuthContext';
import ServiceService from '../services/serviceService';
import {
  PREDEFINED_SERVICE_TYPES,
  IntervalUnit,
  toIntervalDays,
  CreateServiceScheduleData,
} from '../types/Service';
import { ErrorLogger } from '../utils/errorLogger';
import { formatShortDate as formatDate } from '../utils/formatDate';
import { Truck } from '../types/Truck';

interface AddServiceScheduleBottomSheetProps {
  visible: boolean;
  truck: Truck;
  onClose: () => void;
  onScheduleCreated: () => void;
}

const AddServiceScheduleBottomSheet: React.FC<AddServiceScheduleBottomSheetProps> = ({
  visible,
  truck,
  onClose,
  onScheduleCreated,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [serviceType, setServiceType] = useState('');
  const [customType, setCustomType] = useState('');
  const [intervalValue, setIntervalValue] = useState('3');
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('months');
  const [firstDueDate, setFirstDueDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() + toIntervalDays(3, 'months'));
    return d;
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const isCustom = serviceType === '__custom__';
  const effectiveType = isCustom ? customType.trim() : serviceType;

  const updateDueDate = useCallback((val: string, unit: IntervalUnit) => {
    const numVal = parseInt(val, 10) || 1;
    const d = new Date();
    d.setDate(d.getDate() + toIntervalDays(numVal, unit));
    setFirstDueDate(d);
  }, []);

  const handleSave = useCallback(async () => {
    const isCustomType = serviceType === '__custom__';
    const resolvedType = isCustomType ? customType.trim() : serviceType;

    if (!resolvedType) {
      Alert.alert('Error', isCustomType
        ? 'Please enter a custom service name'
        : 'Please select a service type');
      return;
    }
    const numVal = parseInt(intervalValue, 10);
    if (!numVal || numVal <= 0) {
      Alert.alert('Error', 'Please enter a valid interval');
      return;
    }
    if (!user?.uid) return;
    if (!truck.id) {
      Alert.alert('Error', 'Invalid truck — please try again');
      return;
    }

    try {
      setSaving(true);
      const data: CreateServiceScheduleData = {
        truckId: truck.id,
        truckPlateNumber: truck.plateNumber,
        serviceType: resolvedType,
        isCustomType: isCustomType,
        intervalDays: toIntervalDays(numVal, intervalUnit),
        nextDueDate: firstDueDate,
      };
      await ServiceService.createSchedule(user.uid, data);
      onScheduleCreated();
      onClose();
      setServiceType('');
      setCustomType('');
      setIntervalValue('3');
      setIntervalUnit('months');
      const resetDate = new Date();
      resetDate.setDate(resetDate.getDate() + toIntervalDays(3, 'months'));
      setFirstDueDate(resetDate);
    } catch (error) {
      ErrorLogger.logError(error as any, 'AddServiceScheduleBottomSheet.handleSave');
      Alert.alert('Error', 'Failed to save service schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [serviceType, customType, intervalValue, intervalUnit, firstDueDate, user, truck, onScheduleCreated, onClose]);


  return (
    <BaseBottomSheet
      visible={visible}
      onClose={onClose}
      title="Add Service Schedule"
      height={0.8}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={[styles.label, { color: theme.colors.onSurface }]}>Service Type</Text>
        <View style={styles.chipGrid}>
          {PREDEFINED_SERVICE_TYPES.map(type => (
            <Chip
              key={type}
              selected={serviceType === type}
              onPress={() => setServiceType(type)}
              style={styles.chip}
              compact
            >
              {type}
            </Chip>
          ))}
          <Chip
            selected={serviceType === '__custom__'}
            onPress={() => setServiceType('__custom__')}
            style={styles.chip}
            compact
          >
            Other (custom)
          </Chip>
        </View>

        {isCustom && (
          <View style={styles.fieldContainer}>
            <PaperTextInput
              mode="flat"
              label="Custom Service Name"
              value={customType}
              onChangeText={setCustomType}
              style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
            />
          </View>
        )}

        <Text style={[styles.label, { color: theme.colors.onSurface }]}>Interval</Text>
        <View style={styles.intervalRow}>
          <PaperTextInput
            mode="flat"
            label="Every"
            value={intervalValue}
            onChangeText={val => {
              setIntervalValue(val);
              updateDueDate(val, intervalUnit);
            }}
            keyboardType="numeric"
            style={[styles.paperInput, styles.intervalInput, { backgroundColor: theme.colors.surfaceVariant }]}
          />
          <SegmentedButtons
            value={intervalUnit}
            onValueChange={val => {
              const u = val as IntervalUnit;
              setIntervalUnit(u);
              updateDueDate(intervalValue, u);
            }}
            buttons={[
              { value: 'days', label: 'Days' },
              { value: 'weeks', label: 'Wks' },
              { value: 'months', label: 'Mos' },
            ]}
            style={styles.segmented}
          />
        </View>

        <Text style={[styles.label, { color: theme.colors.onSurface }]}>First Due Date</Text>
        <Button
          mode="text"
          onPress={() => setShowDatePicker(true)}
          style={styles.dateButton}
          icon="calendar"
        >
          {formatDate(firstDueDate)}
        </Button>

        <DatePickerModal
          locale="en-GB"
          mode="single"
          visible={showDatePicker}
          onDismiss={() => setShowDatePicker(false)}
          date={firstDueDate}
          onConfirm={({ date }) => {
            setShowDatePicker(false);
            if (date) setFirstDueDate(date);
          }}
        />
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={!effectiveType || saving}
        >
          {saving ? 'Saving...' : 'Add Schedule'}
        </Button>
      </View>
    </BaseBottomSheet>
  );
};

const styles = StyleSheet.create({
  scrollView: { flexShrink: 1 },
  scrollContent: { padding: 16 },
  label: { fontFamily: 'Inter-Medium', fontSize: 14, marginBottom: 8, marginTop: 12 },
  chipGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
  chip: { marginBottom: 4, borderRadius: 9999 },
  fieldContainer: { marginBottom: 8 },
  paperInput: {},
  intervalRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  intervalInput: { width: 80 },
  segmented: { flex: 1 },
  dateButton: { marginBottom: 4 },
  buttonContainer: { padding: 16 },
});

export default AddServiceScheduleBottomSheet;
